import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusCircle, Pencil, Save, PowerOff, Trash2 } from "lucide-react";

const CATEGORIES = ["admission", "bed", "doctor", "nursing", "equipment", "procedure", "investigation", "misc"];
const UNITS = ["per_use", "per_day", "per_hour"];
// Keys the backend auto-accrues (billing_routes._auto_accrue). Surfaced in the UI
// as a small "Auto-billed" badge so admins understand these will appear on every
// patient's bill without manual action.
const AUTO_KEYS = new Set([
  "registration", "admission_fee",
  "bed_l2", "bed_l3", "bed_iso",
  "doctor_visit", "nursing",
  "ventilator", "cpap", "hfnc", "oxygen", "phototherapy",
]);
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

export default function BillingConfig() {
  return (
    <div className="space-y-6" data-testid="billing-config-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Administrator</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Billing configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage charge master and hospital details used on invoices.</p>
      </div>
      <Tabs defaultValue="charges">
        <TabsList className="bg-transparent border-b border-border rounded-none p-0 h-auto">
          <TabsTrigger value="charges" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Charges</TabsTrigger>
          <TabsTrigger value="hospital" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Hospital settings</TabsTrigger>
        </TabsList>
        <TabsContent value="charges" className="pt-6"><ChargesPanel /></TabsContent>
        <TabsContent value="hospital" className="pt-6"><HospitalPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ChargesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/charges");
      setItems(data || []);
    } catch { toast.error("Failed to load charges"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  const toggle = async (row) => {
    try {
      await api.patch(`/billing/charges/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Deactivated" : "Activated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const hardDelete = async (row) => {
    try {
      await api.delete(`/billing/charges/${row.id}?hard=true`);
      toast.success(`Deleted "${row.name}"`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Filter:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-44" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button className="rounded-full" onClick={() => setCreating(true)} data-testid="btn-new-charge">
          <PlusCircle size={14} className="mr-2" /> New charge
        </Button>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Key</th>
              <th className="text-left px-3 py-2 font-medium">Category</th>
              <th className="text-left px-3 py-2 font-medium">Department</th>
              <th className="text-right px-3 py-2 font-medium">Rate</th>
              <th className="text-right px-3 py-2 font-medium">GST %</th>
              <th className="text-left px-3 py-2 font-medium">Unit</th>
              <th className="text-left px-3 py-2 font-medium">Active</th>
              <th className="text-right px-3 py-2 font-medium w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && filtered.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{r.name}</span>
                    {AUTO_KEYS.has(r.key) && (
                      <Badge
                        className="rounded-full text-[10px] bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 border border-emerald-500/30"
                        title="Automatically added to every admitted patient's bill by the system"
                        data-testid={`auto-billed-${r.key}`}
                      >
                        Auto-billed
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground text-[12px] font-mono">{r.key || "—"}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="rounded-full text-[10px]">{r.category}</Badge></td>
                <td className="px-3 py-2">{r.department}</td>
                <td className="px-3 py-2 text-right tabular">{inr(r.rate)}</td>
                <td className="px-3 py-2 text-right tabular">{r.gst_percent || 0}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.unit}</td>
                <td className="px-3 py-2">
                  <Badge className={`rounded-full text-[10px] ${r.active ? "" : "bg-muted text-muted-foreground"}`}>{r.active ? "Yes" : "No"}</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <button className="text-muted-foreground hover:text-foreground mr-2" onClick={() => setEditing(r)} title="Edit" data-testid={`edit-${r.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button className={`mr-2 hover:text-destructive ${r.active ? "text-muted-foreground" : "text-emerald-500"}`} onClick={() => toggle(r)} title={r.active ? "Deactivate" : "Activate"} data-testid={`toggle-${r.id}`}>
                    <PowerOff size={14} />
                  </button>
                  <button className="text-muted-foreground hover:text-destructive" onClick={() => setDeleting(r)} title="Delete permanently" data-testid={`delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No charges.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {(creating || editing) && (
        <ChargeModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
      {deleting && (
        <Dialog open onOpenChange={() => setDeleting(null)}>
          <DialogContent data-testid="delete-charge-modal">
            <DialogHeader><DialogTitle>Delete charge permanently?</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <p>
                You are about to permanently delete <strong>{deleting.name}</strong>
                {deleting.key ? <> <span className="font-mono text-xs text-muted-foreground">({deleting.key})</span></> : null}.
              </p>
              <p className="text-muted-foreground">
                Existing bills that already used this charge will keep working —
                each bill line carries its own name and rate. This charge simply
                won't appear in the picker any more.
                {deleting.key ? " Since this is an auto-billing default, deletion is remembered across server restarts." : ""}
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => hardDelete(deleting)} data-testid="confirm-delete-charge">
                <Trash2 size={14} className="mr-1.5" /> Delete permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ChargeModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    key: initial?.key || "",
    name: initial?.name || "",
    category: initial?.category || "procedure",
    department: initial?.department || "NICU",
    rate: initial?.rate ?? "",
    gst_percent: initial?.gst_percent ?? 0,
    unit: initial?.unit || "per_use",
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name || f.rate === "") return toast.error("Name and rate are required");
    setSaving(true);
    try {
      const body = {
        name: f.name, category: f.category, department: f.department,
        rate: Number(f.rate), gst_percent: Number(f.gst_percent || 0),
        unit: f.unit, active: f.active,
      };
      if (isEdit) {
        await api.patch(`/billing/charges/${initial.id}`, body);
      } else {
        await api.post(`/billing/charges`, { ...body, key: f.key || undefined });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="charge-modal">
        <DialogHeader><DialogTitle>{isEdit ? "Edit charge" : "New charge"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cf-name" /></div>
          {!isEdit && <div className="col-span-2"><Label>Key (internal, optional — used for auto-accrual)</Label><Input value={f.key} onChange={(e) => set("key", e.target.value)} placeholder="e.g., surfactant" /></div>}
          <div>
            <Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Department</Label><Input value={f.department} onChange={(e) => set("department", e.target.value)} /></div>
          <div><Label>Rate (₹) *</Label><Input type="number" step="0.01" value={f.rate} onChange={(e) => set("rate", e.target.value)} data-testid="cf-rate" /></div>
          <div><Label>GST %</Label><Input type="number" step="0.01" value={f.gst_percent} onChange={(e) => set("gst_percent", e.target.value)} /></div>
          <div>
            <Label>Unit</Label>
            <Select value={f.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
            <Label htmlFor="active">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-charge"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HospitalPanel() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/billing/settings").then(({ data }) => setF(data)).catch(() => toast.error("Failed"));
  }, []);
  if (!f) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/billing/settings", {
        name: f.name, address: f.address, phone: f.phone, email: f.email, gstin: f.gstin,
        invoice_prefix: f.invoice_prefix, receipt_prefix: f.receipt_prefix,
        outstanding_limit_inr: Number(f.outstanding_limit_inr || 0),
        signature_line: f.signature_line,
      });
      setF(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
      <div className="col-span-2"><Label>Hospital name</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="hs-name" /></div>
      <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
      <div><Label>Phone</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
      <div><Label>Email</Label><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
      <div><Label>GSTIN</Label><Input value={f.gstin || ""} onChange={(e) => set("gstin", e.target.value)} /></div>
      <div><Label>Signature line</Label><Input value={f.signature_line || ""} onChange={(e) => set("signature_line", e.target.value)} /></div>
      <div><Label>Invoice # prefix</Label><Input value={f.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} /></div>
      <div><Label>Receipt # prefix</Label><Input value={f.receipt_prefix || ""} onChange={(e) => set("receipt_prefix", e.target.value)} /></div>
      <div><Label>Outstanding warning limit (₹)</Label><Input type="number" value={f.outstanding_limit_inr ?? ""} onChange={(e) => set("outstanding_limit_inr", e.target.value)} data-testid="hs-limit" /></div>
      <div className="col-span-2 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="btn-save-hospital"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
